// Sugalarm — Cycle 242 per-zone custom alarm/recovery sound manifest.
//
// The SINGLE source the Settings dropdowns render from and the SINGLE
// resolver `useAlarmSounds` uses to turn a stored setting id into a file
// path. "Default" is always the first option per zone and resolves to the
// zone's ORIGINAL hardcoded file (also the mandatory fallback if a custom
// file ever fails to load — an alarm must never be silent).
//
// The skin ids mirror the server allow-list in
// `server/schemas/settings.py` (`ALARM_SOUND_OPTION_IDS`); keep the two in
// sync. Skin files are MP3 under `/sounds/alert-options/` (webview-playable
// on every surface incl. iOS WKWebView — never OGG).

export type AlarmZone =
  | "urgent_low"
  | "low"
  | "inrange"
  | "high"
  | "urgent_high";

export interface AlarmSoundOption {
  id: string;
  label: string;
  file: string;
}

/** The five `members.settings` keys, one per zone. */
export const ALARM_SOUND_SETTING_KEY: Record<AlarmZone, string> = {
  urgent_low: "alarm_sound_urgent_low",
  low: "alarm_sound_low",
  inrange: "alarm_sound_inrange",
  high: "alarm_sound_high",
  urgent_high: "alarm_sound_urgent_high",
};

/** The original hardcoded file per zone — the "default" option AND the
 *  guaranteed fallback. Must match `useAlarmSounds`' original SRC map. */
export const ALARM_SOUND_DEFAULT_FILE: Record<AlarmZone, string> = {
  urgent_low: "/sounds/urgentLow.wav",
  low: "/sounds/lowNotify.wav",
  inrange: "/sounds/normal.wav",
  high: "/sounds/high.wav",
  urgent_high: "/sounds/high.wav",
};

const skin = (id: string): string => `/sounds/alert-options/${id}.mp3`;

function zone(z: AlarmZone, skins: Array<[string, string]>): AlarmSoundOption[] {
  return [
    { id: "default", label: "Default", file: ALARM_SOUND_DEFAULT_FILE[z] },
    ...skins.map(([id, label]) => ({ id, label, file: skin(id) })),
  ];
}

/** Ordered options per zone — Default first, then the curated skins. */
export const ALARM_SOUND_OPTIONS: Record<AlarmZone, AlarmSoundOption[]> = {
  urgent_low: zone("urgent_low", [
    ["urgentlow_fastpaced", "Fast-paced"],
    ["urgentlow_electro", "Electro"],
    ["urgentlow_80srock", "80s Rock"],
    ["urgentlow_long", "Long"],
  ]),
  low: zone("low", [
    ["low_female", "Female"],
    ["low_reggae", "Reggae"],
    ["low_country", "Country"],
    ["low_80s", "80s"],
    ["low_90spunk", "90s Punk"],
  ]),
  inrange: zone("inrange", [
    ["inrange_peaceful", "Peaceful"],
    ["inrange_angel", "Angel"],
    ["inrange_60s", "60s"],
    ["inrange_reggae", "Reggae"],
    ["inrange_country", "Country"],
    ["inrange_80s", "80s"],
  ]),
  high: zone("high", [
    ["high_jazz", "Jazz"],
    ["high_country", "Country"],
    ["high_pop", "Pop"],
    ["high_80s", "80s"],
    ["high_metalpunk", "Metal-Punk"],
  ]),
  urgent_high: zone("urgent_high", [
    ["urgenthigh_fastpaced", "Fast-paced"],
    ["urgenthigh_pop", "Pop"],
    ["urgenthigh_ska", "Ska"],
  ]),
};

/** Resolve a stored setting id to a playable file path for a zone.
 *  Unknown / "default" / null → the zone's guaranteed default file. */
export function resolveAlarmSoundFile(
  z: AlarmZone,
  id: string | null | undefined,
): string {
  if (id && id !== "default") {
    const found = ALARM_SOUND_OPTIONS[z].find((o) => o.id === id);
    if (found) return found.file;
  }
  return ALARM_SOUND_DEFAULT_FILE[z];
}
